// A replay's register page settings, on each saved webinar in Webinars (docs/replay-register-spec.md 5,
// Adam's cut-down version of 23 Oct): the Link (the slug, with the full address and a Copy button, and
// the line about ?src=), the Name in MailerLite (showing the group it makes), the switch, refused with a
// plain reason when the replay isn't up, and Registrations (n), which opens who registered (owner and
// admin: it shows emails). The editor keeps what's typed and saves it with the webinar's own Save
// (WebinarsView.jsx), writing only the fields that changed. Undrawn: no board has it.
import { useState } from 'react'
import { ButtonLink, Button, Field, TextInput, adminPath } from '../ui/index.js'
import { copyText } from '../../lib/clipboard.js'
import { COPY } from './copy.js'
import { groupName, registerLink } from './model.js'
import { isSlug, isListName } from '../../free/register.js'

// "?src=ml-oct-replay" kept on 1 line, so the line never breaks after its question mark
const noBreak = text => String(text).split(/(\?src=\S*?)(?=[\s.,]*(?:\s|$))/).map((bit, i) => (i % 2 ? <span key={i} className="adc-nowrap">{bit}</span> : bit))

export function RegisterPart({ ids, edits, oa, webinarId, savedSlug, count, slug, name, on, refused, notShown, onSlug, onName, onToggle }) {
  const [copied, setCopied] = useState(false)
  const good = isSlug(slug.trim())
  const link = good ? registerLink(slug.trim()) : null
  async function copy() {
    if (!link) return
    setCopied(await copyText(link))
  }
  return (
    <div className="adc-rr" role="group" aria-labelledby={`${ids}-rr`}>
      <div className="adc-rr-head">
        <span className="adc-field-label" id={`${ids}-rr`}>{COPY.rrPage}</span>
        <span className="adc-hint">{COPY.rrLede}</span>
      </div>

      <Field label={COPY.rrSlug} hint={COPY.rrSlugHint} plain>
        {props => (
          <span className="adc-rr-slug">
            <span className="adc-rr-prefix" aria-hidden="true">{COPY.rrSlugPrefix}</span>
            <TextInput {...props} value={slug} onChange={v => { setCopied(false); onSlug(v) }} placeholder={COPY.rrSlugPlaceholder}
              disabled={!edits} autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={60} />
          </span>
        )}
      </Field>
      {link && (
        <div className="adc-rr-address">
          <span className="adc-rr-address-label">{COPY.rrAddress}</span>
          <span className="adc-rr-address-row">
            <a href={link} target="_blank" rel="noopener noreferrer" className="adc-rr-link adc-break">{link}</a>
            <Button size="sm" onClick={copy}>{copied ? COPY.rrCopied : COPY.rrCopy}</Button>
          </span>
          <span className="adc-hint">{noBreak(COPY.rrSource)}</span>
        </div>
      )}

      <Field label={COPY.rrName} hint={isListName(name) ? COPY.rrNameHint(groupName(name)) : COPY.rrNameEmpty} plain>
        {props => <TextInput {...props} value={name} onChange={onName} disabled={!edits} maxLength={60} autoComplete="off" />}
      </Field>

      <div className="adc-rr-switch">
        <button type="button" role="switch" aria-checked={on} className="adc-showrow" disabled={!edits}
          aria-describedby={refused ? `${ids}-rr-no` : undefined} onClick={onToggle}>
          <span className="adc-showrow-text">
            <span className="adc-showrow-title">{COPY.rrOn}</span>
            <span className="adc-showrow-note">{on ? COPY.rrOnNote : COPY.rrOffNote}</span>
          </span>
          <span className="adc-track" aria-hidden="true"><span className="adc-knob" /></span>
        </button>
        {refused && <p className="adc-show-warn" id={`${ids}-rr-no`} role="alert">{refused}</p>}
        {on && notShown && !refused && <p className="adc-show-warn">{COPY.rrNotShown}</p>}
      </div>

      {oa && savedSlug && (
        <div className="adc-rr-foot">
          <ButtonLink size="sm" to={adminPath.webinarRegistrations(webinarId)}>{COPY.rrList(count || 0)}</ButtonLink>
        </div>
      )}
    </div>
  )
}
